/**
 * Turning a machine key into something a person can read (#1201).
 *
 * Some surfaces render keys that are genuinely open-ended — an automation
 * rule's `config` object, a form's `customer_fields` — so there is no label map
 * to write. The failure mode is rendering them raw: the office sees
 * `site_contact_phone: 817…` or `{"days_until_followup":30,"tem…`, which reads
 * as somebody's internal tool rather than a product.
 *
 * Sentence-casing is not as good as a real human label. Where a fixed
 * vocabulary exists — integrations, task types, job statuses — write the label
 * map instead. This is for the open-ended cases only.
 */

/** `site_contact_phone` → `Site contact phone`. Also handles dots and camelCase. */
export function humanFieldName(key: string): string {
    const words = key
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .replace(/[_.]+/g, " ")
        .trim()
        .split(/\s+/)
        .filter(Boolean);
    if (words.length === 0) return "";
    // Sentence case, not Title Case: "Days until followup", not "Days Until
    // Followup". An all-caps word (ID, PO, WO) is left alone.
    return words
        .map((w, i) => {
            if (w === w.toUpperCase() && w.length <= 3) return w;
            const lower = w.toLowerCase();
            return i === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
        })
        .join(" ");
}

/**
 * A record's label when its business number may be missing (#1201).
 *
 * The idiom this replaces is everywhere: `invoice_number ?? id.slice(0, 8)`.
 * It reads as a sensible fallback and renders "3f9a2b1c" — a fragment of a
 * primary key, which a person can neither search nor quote down the phone.
 *
 * When there is no number, say so in words. "Invoice — not numbered yet" tells
 * the reader something true; "3f9a2b1c" tells them nothing.
 */
export function recordLabel(kind: string, businessNumber?: string | null, who?: string | null): string {
    const number = (businessNumber ?? "").trim();
    const name = (who ?? "").trim();
    if (number) return name ? `${kind} ${number} — ${name}` : `${kind} ${number}`;
    if (name) return `${kind} for ${name}`;
    return `${kind} — not numbered yet`;
}

/**
 * One changed value, as a person would read it (#1201).
 *
 * `JSON.stringify` is the reflex here and it leaks its own syntax: a price
 * becomes `"12.50"` with the quotes showing, `null` becomes the word null, and
 * an object becomes a brace blob. Audit diffs are read by the office, not by a
 * parser.
 */
export function displayValue(v: unknown): string {
    if (v === null || v === undefined || v === "") return "—";
    if (typeof v === "boolean") return v ? "Yes" : "No";
    if (Array.isArray(v)) return v.length === 0 ? "—" : v.map(displayValue).join(", ");
    if (typeof v === "object") {
        const parts = Object.entries(v as Record<string, unknown>).map(
            ([k, val]) => `${humanFieldName(k)}: ${displayValue(val)}`,
        );
        return parts.length ? parts.join(" · ") : "—";
    }
    return String(v);
}
